import { Edge, Node } from 'reactflow';
import useSourceNode from '@components/EntityDrawers/hooks/useSourceNode';
import useStore, { RFState } from '@components/MainStage/store';
import { EntityData } from '@components/MainStage/types';
import { SubLayerTypes } from '@constants/canvas/layers';
import { RefType } from '@constants/entities/project';
import useCurrentAsset from '@hooks/useCurrentAsset';
import useProjectData from '@hooks/useProjectData';
import useProjectSettings from '@hooks/useProjectSettings';
import useReferenceMaps from '@hooks/useReferenceMaps';
import { AssessmentMetaData, NodeDTO } from '@store/services/nodes/types';
import { IDomain, ISettings } from '@store/services/projects/types';
import { CIASettingsType } from '@store/services/questionnaire/types';
import { shallow } from 'zustand/shallow';

const graphSelector = (state: RFState) => ({
  nodes: state.nodes,
  edges: state.edges,
});

export type TransformContext = {
  subLayer: SubLayerTypes | undefined;
  qnousMap: Map<string, string>;
  nistMap: Map<string, string>;
  isoMap: Map<string, string>;
  ismMap: Map<string, string>;
  cisMap: Map<string, string>;
  settings: Partial<ISettings>;
  nodes: Node[];
  edges: Edge[];
  activeNode: Node<EntityData<NodeDTO<any>>> | undefined;
  asset: NodeDTO<AssessmentMetaData>;
  ciaSettings: CIASettingsType;
  domains: IDomain[];
};

const useTransformContext = () => {
  const activeNode = useSourceNode() as Node<EntityData<NodeDTO<any>>>;
  const [{ qnousMap, nistMap, isoMap, ismMap, cisMap }] = useReferenceMaps();
  const { settings, domains } = useProjectSettings();
  const { nodes, edges } = useStore(graphSelector, shallow);
  const { dto: asset } = useCurrentAsset();
  const [{ questionnaire }] = useProjectData();

  const context: TransformContext = {
    activeNode,
    subLayer: activeNode?.parentNode as SubLayerTypes,
    qnousMap,
    nistMap,
    isoMap,
    ismMap,
    cisMap,
    settings,
    nodes,
    edges,
    asset,
    ciaSettings: questionnaire?.cia_criteria ?? {
      assurance_level: { criterias: [] },
      coverage: { criterias: [] },
      applicability: { criterias: [] },
      prioritisation: {
        question_id: '',
        model: RefType.NIST,
        answer: '',
      },
      levels_based_on_questionnaire: false,
    },
    domains: domains ?? [],
  };

  return { context };
};

export default useTransformContext;
